<template>
  <Teleport to="body">
    <div v-if="isOpen" class="fixed inset-0 z-[10000] flex items-center justify-center p-4">
      <div class="absolute inset-0 bg-black/85 backdrop-blur-sm" @click="closeModal"></div>

      <div class="relative w-full max-w-6xl h-[92vh] max-h-[92vh] overflow-hidden rounded-2xl border border-white/10 bg-casino-card flex flex-col">
        <div class="px-6 py-5 border-b border-white/10 bg-casino-card">
          <div class="flex items-center justify-between gap-4">
            <div class="flex items-center gap-4 min-w-0">
              <div class="w-11 h-11 rounded-xl bg-casino-primary/20 border border-casino-primary/30 flex items-center justify-center flex-shrink-0">
                <Wallet class="w-5 h-5 text-casino-primary-light" />
              </div>
              <div class="min-w-0">
                <h2 class="text-xl font-bold text-white truncate">{{ t('wallet.title') }}</h2>
                <p class="text-xs text-gray-400 truncate">{{ t('wallet.subtitle') }}</p>
              </div>
            </div>

            <div class="flex items-center gap-3">
              <div class="flex items-center gap-3 bg-white/5 rounded-xl px-4 py-2 border border-white/10">
                <Coins class="w-5 h-5 text-casino-win" />
                <div class="text-right">
                  <p class="text-[10px] text-gray-500 uppercase tracking-wide">{{ t('wallet.balance') }}</p>
                  <p class="text-lg font-bold text-white tabular-nums">{{ currencySymbol }}{{ displayBalance.toFixed(2) }}</p>
                </div>
              </div>

              <button @click="closeModal" class="btn-icon p-2">
                <X class="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        <div class="flex border-b border-white/10 px-6 bg-casino-card">
          <button
            @click="activeTab = 'deposit'"
            :class="[
              'flex-1 py-4 text-sm font-semibold transition-colors flex items-center justify-center gap-2 border-b-2',
              activeTab === 'deposit'
                ? 'text-casino-win border-casino-win'
                : 'text-gray-400 border-transparent hover:text-white'
            ]"
          >
            <ArrowDownLeft class="w-4 h-4" />
            {{ t('wallet.depositTab') }}
          </button>
          <button
            @click="activeTab = 'withdraw'"
            :class="[
              'flex-1 py-4 text-sm font-semibold transition-colors flex items-center justify-center gap-2 border-b-2',
              activeTab === 'withdraw'
                ? 'text-casino-loss border-casino-loss'
                : 'text-gray-400 border-transparent hover:text-white'
            ]"
          >
            <ArrowUpRight class="w-4 h-4" />
            {{ t('wallet.withdrawTab') }}
          </button>
        </div>

        <div class="flex-1 min-h-0 overflow-hidden p-5 md:p-6">
          <div class="wallet-panels-grid grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-6 h-full min-h-0">
            <aside
              v-if="showSidebar"
              class="wallet-sidebar h-full min-h-0 flex flex-col overflow-hidden rounded-2xl border border-white/10 bg-casino-card"
            >
              <div class="px-4 py-4 border-b border-white/10">
                <h3 class="text-xs font-semibold uppercase tracking-[0.12em] text-gray-400">
                  {{ activeTab === 'deposit' ? t('wallet.depositMethods') : t('wallet.withdrawMethods') }}
                </h3>
              </div>

              <div v-if="methodsLoading" class="wallet-methods-scroll px-3 py-3 pr-2 space-y-2">
                <div
                  v-for="idx in 7"
                  :key="`wallet-method-skeleton-${idx}`"
                  class="w-full rounded-xl border border-white/10 bg-white/[0.02] px-3 py-3"
                >
                  <div class="flex items-center gap-3">
                    <SkeletonBlock custom-class="w-10 h-10 rounded-lg" />
                    <div class="flex-1 space-y-2">
                      <SkeletonBlock custom-class="h-3 w-24" />
                      <SkeletonBlock custom-class="h-3 w-36" />
                    </div>
                  </div>
                </div>
              </div>

              <div v-else-if="methodsError" class="flex-1 min-h-0 flex items-center justify-center text-sm text-casino-loss text-center px-4 py-3">
                {{ methodsError }}
              </div>

              <div v-else-if="filteredMethods.length === 0" class="flex-1 min-h-0 flex items-center justify-center text-sm text-gray-400 text-center px-4 py-3">
                {{ activeTab === 'deposit' ? t('wallet.noDepositMethods') : t('wallet.noWithdrawMethods') }}
              </div>

              <div v-else class="wallet-methods-scroll px-3 py-3 pr-2 space-y-2">
                <button
                  v-for="method in filteredMethods"
                  :key="method.id"
                  @click="selectMethod(method)"
                  :disabled="method.disabled"
                  :class="[
                    'w-full text-left rounded-xl border transition-all duration-200 px-3 py-3',
                    selectedMethod === method.id
                      ? 'border-casino-primary/60 bg-white/10'
                      : 'border-white/10 bg-white/[0.02] hover:bg-white/5 hover:border-white/20',
                    method.disabled ? 'opacity-55 cursor-not-allowed' : ''
                  ]"
                >
                  <div class="flex items-center gap-3">
                    <div class="w-10 h-10 rounded-lg bg-black/20 border border-white/10 flex items-center justify-center flex-shrink-0 overflow-hidden">
                      <img
                        v-if="resolveMethodImage(method)"
                        :src="resolveMethodImage(method)"
                        :alt="t('wallet.logoAlt', { name: method.title })"
                        class="w-7 h-7 object-contain"
                        @error="markImageError(method)"
                      />
                      <Wallet v-else class="w-4 h-4 text-gray-400" />
                    </div>

                    <div class="flex-1 min-w-0">
                      <p class="text-sm font-semibold text-white truncate">{{ method.title }}</p>
                      <p class="text-xs text-gray-500 truncate">{{ method.cardSubtitle }}</p>
                      <p v-if="method.disabledReason" class="text-[11px] text-casino-loss mt-1">{{ method.disabledReason }}</p>
                    </div>

                    <CheckCircle2
                      v-if="selectedMethod === method.id"
                      class="w-4 h-4 text-casino-primary flex-shrink-0"
                    />
                  </div>
                </button>
              </div>
            </aside>

            <section
              v-if="showFormPanel"
              class="wallet-form-panel h-full min-h-0 flex flex-col overflow-hidden rounded-2xl border border-white/10 bg-casino-card"
            >
              <div class="flex-1 min-h-0 overflow-y-auto p-5 md:p-6 space-y-5">
                <div v-if="isCryptoCheckoutVisible" class="space-y-4">
                  <div class="rounded-xl border border-white/10 bg-black/20 p-5">
                    <div class="flex items-start justify-between gap-3">
                      <div>
                        <h4 class="text-base font-bold text-white">{{ t('wallet.crypto.scanTitle') }}</h4>
                        <p class="text-sm text-gray-400 mt-1">{{ t('wallet.crypto.keepOpen') }}</p>
                      </div>
                      <button
                        v-if="isMobileLayout"
                        type="button"
                        class="inline-flex items-center justify-center w-8 h-8 rounded-md bg-white/8 hover:bg-white/15 text-gray-300 hover:text-white transition-colors flex-shrink-0"
                        @click="mobilePanel = 'methods'"
                        :aria-label="t('wallet.backToMethods')"
                      >
                        <ArrowLeft class="w-4 h-4" />
                      </button>
                    </div>

                    <div v-if="cryptoQrCodeUrl" class="mt-4 flex justify-center">
                      <div class="rounded-xl border border-white/10 bg-white p-3">
                        <img
                          :src="cryptoQrCodeUrl"
                          :alt="t('wallet.crypto.qrAlt')"
                          class="w-52 h-52 object-contain"
                        />
                      </div>
                    </div>

                    <div class="mt-4 rounded-lg border border-white/10 bg-white/[0.04] p-3 space-y-2 text-sm">
                      <div class="flex justify-between gap-3">
                        <span class="text-gray-400">{{ t('wallet.crypto.crypto') }}</span>
                        <span class="text-white font-semibold">{{ externalResult?.payCurrency || selectedCryptoTicker || 'N/A' }}</span>
                      </div>
                      <div class="flex justify-between gap-3">
                        <span class="text-gray-400">{{ t('wallet.crypto.amountToDeposit') }}</span>
                        <span class="text-white font-semibold">{{ externalResult?.payAmount || '--' }}</span>
                      </div>
                    </div>

                    <div class="mt-3 rounded-lg border border-white/10 bg-white/[0.03] p-3">
                      <p class="text-xs text-gray-400 uppercase tracking-wide">{{ t('wallet.paymentAddress') }}</p>
                      <p class="text-sm text-white break-all mt-1">{{ externalResult?.payAddress || t('common.notAvailable') }}</p>
                    </div>

                    <div class="mt-4 flex justify-end">
                      <button
                        type="button"
                        class="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-sm text-white transition-colors"
                        @click="startNewCryptoDeposit"
                      >
                        {{ t('wallet.crypto.newDeposit') }}
                      </button>
                    </div>
                  </div>
                </div>

                <template v-else>
                <div v-if="methodsLoading" class="rounded-xl border border-white/10 bg-black/20 p-4">
                  <div class="flex items-start gap-4">
                    <SkeletonBlock custom-class="w-14 h-14 rounded-xl" />
                    <div class="min-w-0 flex-1 space-y-2">
                      <SkeletonBlock custom-class="h-4 w-32" />
                      <SkeletonBlock custom-class="h-3 w-56" />
                      <div class="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
                        <SkeletonBlock custom-class="h-12 rounded-lg" />
                        <SkeletonBlock custom-class="h-12 rounded-lg" />
                        <SkeletonBlock custom-class="h-12 rounded-lg" />
                      </div>
                    </div>
                  </div>
                </div>

                <div v-else-if="selectedMethodInfo" class="method-hero rounded-xl p-4 md:p-5">
                    <div class="flex items-start justify-between gap-3">
                      <div class="flex items-start gap-4 min-w-0">
                        <div class="w-14 h-14 rounded-xl bg-black/30 border border-white/10 flex items-center justify-center flex-shrink-0 overflow-hidden">
                          <img
                            v-if="resolveMethodImage(selectedMethodInfo)"
                            :src="resolveMethodImage(selectedMethodInfo)"
                            :alt="t('wallet.logoAlt', { name: selectedMethodInfo.title })"
                            class="w-10 h-10 object-contain"
                            @error="markImageError(selectedMethodInfo)"
                          />
                          <Wallet v-else class="w-5 h-5 text-gray-400" />
                        </div>

                        <div class="min-w-0">
                          <div class="flex items-center gap-2 flex-wrap">
                            <h4 class="text-lg font-bold text-white truncate">{{ selectedMethodInfo.title }}</h4>
                            <span class="method-chip method-chip-muted">{{ activeTab === 'deposit' ? t('transactions.types.deposit') : t('transactions.types.withdrawal') }}</span>
                          </div>
                          <p class="text-sm text-gray-400 mt-1 leading-relaxed">{{ selectedMethodInfo.description }}</p>
                        </div>
                      </div>
                      <button
                        v-if="isMobileLayout"
                        type="button"
                        class="inline-flex items-center justify-center w-8 h-8 rounded-md bg-white/8 hover:bg-white/15 text-gray-300 hover:text-white transition-colors flex-shrink-0"
                        @click="mobilePanel = 'methods'"
                        :aria-label="t('wallet.backToMethods')"
                      >
                        <ArrowLeft class="w-4 h-4" />
                      </button>
                    </div>

                    <div class="method-metrics mt-4 rounded-lg border border-white/10 bg-white/[0.03]">
                      <div class="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-white/10">
                        <div class="px-3 py-3">
                          <p class="text-gray-500 uppercase tracking-wide text-[11px]">{{ t('wallet.operatingCurrency') }}</p>
                          <p class="text-white font-semibold mt-1 text-base">{{ userCurrency }}</p>
                        </div>
                        <div class="px-3 py-3">
                          <p class="text-gray-500 uppercase tracking-wide text-[11px]">{{ t('wallet.minAllowed') }}</p>
                          <p class="text-white font-semibold mt-1 text-base">{{ currencySymbol }}{{ currentMin }}</p>
                        </div>
                        <div class="px-3 py-3">
                          <p class="text-gray-500 uppercase tracking-wide text-[11px]">{{ t('wallet.maxAllowed') }}</p>
                          <p class="text-white font-semibold mt-1 text-base">{{ Number.isFinite(currentMax) ? `${currencySymbol}${currentMax}` : t('wallet.noLimit') }}</p>
                        </div>
                      </div>
                    </div>
                </div>

                <div
                  v-if="showManualProofUploader && manualDepositBankFields.length > 0"
                  class="rounded-xl border border-white/10 bg-black/20 p-4"
                >
                  <button
                    type="button"
                    class="w-full flex items-center justify-between gap-3 text-left"
                    @click="showManualTransferDetails = !showManualTransferDetails"
                  >
                    <div>
                      <h4 class="text-sm font-semibold text-white">{{ t('wallet.manual.transferDataTitle') }}</h4>
                      <p class="text-xs text-gray-400 mt-0.5">
                        {{ t('wallet.manual.transferDataText') }}
                      </p>
                    </div>
                    <ChevronDown
                      class="w-4 h-4 text-gray-400 transition-transform duration-200 flex-shrink-0"
                      :class="showManualTransferDetails ? 'rotate-180' : ''"
                    />
                  </button>

                  <Transition name="collapse-fade">
                    <div v-if="showManualTransferDetails" class="space-y-2 mt-3">
                      <div
                        v-for="entry in manualDepositBankFields"
                        :key="entry.key"
                        class="rounded-lg bg-white/[0.05] px-3 py-2 flex items-center justify-between gap-3"
                      >
                        <div class="min-w-0">
                          <p class="text-[11px] text-gray-500 uppercase tracking-wide">{{ entry.label }}</p>
                          <p class="text-sm text-white break-all">{{ entry.value }}</p>
                        </div>

                        <button
                          type="button"
                          class="px-2 py-1 rounded-md bg-white/10 hover:bg-white/15 text-xs text-white transition-colors flex-shrink-0"
                          @click="copyManualField(entry)"
                        >
                          {{ manualCopiedField === entry.key ? t('common.copied') : t('common.copy') }}
                        </button>
                      </div>
                    </div>
                  </Transition>
                </div>

                <div class="space-y-4">
                  <label class="block text-sm font-medium text-gray-300">
                    {{ activeTab === 'deposit' ? t('wallet.enterDepositAmount', { currency: userCurrency }) : t('wallet.enterWithdrawAmount', { currency: userCurrency }) }}
                  </label>

                  <div class="relative">
                    <span class="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-xl font-medium">{{ currencySymbol }}</span>
                    <input
                      v-model="amount"
                      type="number"
                      :min="currentMin"
                      :max="maxAmountAttribute"
                      class="input pl-10 text-xl h-14 font-semibold"
                      placeholder="0.00"
                      :disabled="!selectedMethodInfo || selectedMethodInfo.disabled"
                    />
                  </div>

                  <div class="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <button
                      v-for="quickAmount in quickAmounts"
                      :key="quickAmount"
                      @click="amount = String(quickAmount)"
                      :disabled="isQuickAmountDisabled(quickAmount)"
                      :class="[
                        'py-3 text-sm font-semibold rounded-xl transition-colors',
                        Number(amount) === quickAmount
                          ? 'bg-casino-primary text-white'
                          : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white',
                        isQuickAmountDisabled(quickAmount)
                          ? 'opacity-30 cursor-not-allowed'
                          : ''
                      ]"
                    >
                      {{ currencySymbol }}{{ quickAmount }}
                    </button>
                  </div>
                </div>

                <div v-if="dynamicFields.length > 0" class="rounded-xl border border-white/10 bg-black/20 p-4 space-y-4">
                  <h4 class="text-sm font-semibold text-white">{{ t('wallet.additionalData') }}</h4>

                  <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div v-for="field in dynamicFields" :key="field.key" class="space-y-1">
                      <label class="text-xs text-gray-300">
                        {{ field.label }}
                        <span v-if="field.required" class="text-casino-loss">*</span>
                      </label>
                      <input
                        v-model="fieldValues[field.key]"
                        :type="field.type"
                        :placeholder="field.placeholder"
                        :maxlength="field.maxLength || field.exactLength || undefined"
                        class="input h-11"
                      />
                    </div>
                  </div>
                </div>

                <div v-if="showManualProofUploader" class="rounded-xl border border-white/10 bg-black/20 p-4 space-y-3">
                  <div class="flex items-start justify-between gap-3">
                    <div>
                      <h4 class="text-sm font-semibold text-white">{{ t('wallet.manual.proofTitle') }}</h4>
                      <p class="text-xs text-gray-400 mt-1">{{ t('wallet.manual.proofHint') }}</p>
                    </div>
                  </div>

                  <div class="flex flex-wrap items-center gap-2">
                    <label class="inline-flex items-center px-3 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-sm text-white cursor-pointer transition-colors">
                      <input
                        type="file"
                        class="hidden"
                        accept="image/*,.pdf"
                        @change="handleManualProofUpload"
                        :disabled="manualProofLoading"
                      />
                      {{ manualProofLoading ? t('wallet.manual.processingFile') : t('wallet.manual.attachFile') }}
                    </label>

                    <button
                      v-if="manualProofDocument"
                      type="button"
                      class="px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-xs text-gray-200 transition-colors"
                      @click="clearManualProof"
                    >
                      {{ t('wallet.manual.removeAttachment') }}
                    </button>
                  </div>

                  <p v-if="manualProofFileName" class="text-xs text-casino-win">{{ t('wallet.manual.fileLoaded', { name: manualProofFileName }) }}</p>
                  <p v-if="manualProofError" class="text-xs text-casino-loss">{{ manualProofError }}</p>
                </div>

                <div v-if="isCryptoFlow" class="rounded-xl border border-white/10 bg-black/20 p-4 space-y-4">
                  <div class="flex items-center justify-between gap-3">
                    <h4 class="text-sm font-semibold text-white">{{ t('wallet.crypto.settingsTitle') }}</h4>
                  </div>

                  <div class="space-y-1">
                    <label class="text-xs text-gray-300">{{ t('wallet.crypto.currency') }} <span class="text-casino-loss">*</span></label>
                    <template v-if="isMobileLayout">
                      <button
                        type="button"
                        class="input h-12 text-base flex items-center justify-between"
                        :disabled="cryptoLoading"
                        @click="showCryptoOptions = !showCryptoOptions"
                      >
                        <span class="truncate">{{ selectedCryptoLabel || t('wallet.crypto.selectCurrency') }}</span>
                        <ChevronDown class="w-4 h-4 text-gray-400 transition-transform duration-200" :class="showCryptoOptions ? 'rotate-180' : ''" />
                      </button>

                      <div
                        v-if="showCryptoOptions && cryptoCurrencies.length > 0"
                        class="mt-2 rounded-lg border border-white/10 bg-[#0E1522] p-1.5 max-h-56 overflow-y-auto"
                      >
                        <button
                          v-for="crypto in cryptoCurrencies"
                          :key="`crypto-mobile-${crypto.ticker}`"
                          type="button"
                          class="w-full text-left px-3 py-2.5 rounded-md text-sm text-gray-200 hover:bg-white/10 transition-colors"
                          :class="selectedCryptoTicker === crypto.ticker ? 'bg-casino-primary/20 text-white border border-casino-primary/30' : 'border border-transparent'"
                          @click="selectCryptoTicker(crypto.ticker)"
                        >
                          {{ crypto.fullName || crypto.ticker }} ({{ crypto.ticker }})
                        </button>
                      </div>
                    </template>
                    <select
                      v-else
                      v-model="selectedCryptoTicker"
                      class="input h-11"
                      :disabled="cryptoLoading"
                    >
                      <option value="">{{ t('wallet.crypto.selectCurrency') }}</option>
                      <option v-for="crypto in cryptoCurrencies" :key="crypto.ticker" :value="crypto.ticker">
                        {{ crypto.fullName || crypto.ticker }} ({{ crypto.ticker }})
                      </option>
                    </select>
                  </div>

                  <div v-if="activeTab === 'withdraw'" class="space-y-3">
                    <div class="space-y-1">
                      <label class="text-xs text-gray-300">{{ t('wallet.fields.payoutAddress.label') }} <span class="text-casino-loss">*</span></label>
                      <input
                        v-model="fieldValues.payoutAddress"
                        type="text"
                        :placeholder="t('wallet.crypto.destinationPlaceholder')"
                        class="input h-11"
                      />
                    </div>

                    <div v-if="showPayoutTag" class="space-y-1">
                      <label class="text-xs text-gray-300">{{ t('wallet.fields.payoutExtraId.label') }} <span class="text-casino-loss">*</span></label>
                      <input
                        v-model="fieldValues.payoutExtraId"
                        type="text"
                        :placeholder="t('wallet.crypto.tagPlaceholder')"
                        class="input h-11"
                      />
                    </div>
                  </div>

                  <p v-if="cryptoLoading" class="text-xs text-gray-400">{{ t('wallet.crypto.loading') }}</p>
                </div>

                <div class="rounded-xl border border-white/10 bg-black/20 p-4">
                  <h4 class="font-semibold text-white mb-4 flex items-center gap-2">
                    <Receipt class="w-4 h-4 text-casino-primary" />
                    {{ t('wallet.summary') }}
                  </h4>

                  <div class="space-y-3 text-sm">
                    <div class="flex justify-between py-2">
                      <span class="text-gray-400">{{ t('wallet.amount') }}</span>
                      <span class="text-white font-medium">{{ currencySymbol }}{{ amount || '0.00' }}</span>
                    </div>

                    <div class="border-t border-white/10 pt-3 mt-2">
                      <div class="flex justify-between items-center">
                        <span class="text-gray-300 font-medium">
                          {{ activeTab === 'deposit' ? t('wallet.totalToPay') : t('wallet.youWillReceive') }}
                        </span>
                        <span
                          class="text-2xl font-bold"
                          :class="activeTab === 'deposit' ? 'text-white' : 'text-casino-win'"
                        >
                          {{ currencySymbol }}{{ totalAmount }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <p v-if="formError" class="text-sm text-casino-loss flex items-center gap-2">
                  <AlertCircle class="w-4 h-4" />
                  {{ formError }}
                </p>
                <p v-if="formSuccess" class="text-sm text-casino-win">{{ formSuccess }}</p>

                <div
                  v-if="activeTab === 'withdraw' && withdrawBonusWarning"
                  class="rounded-xl border border-casino-gold/40 bg-casino-gold/10 p-4 space-y-3"
                >
                  <div class="flex items-start gap-3">
                    <AlertCircle class="w-5 h-5 text-casino-gold flex-shrink-0 mt-0.5" />
                    <div class="min-w-0">
                      <h4 class="text-sm font-semibold text-white">{{ t('wallet.bonusWarning.title') }}</h4>
                      <p class="text-sm text-gray-300 mt-1">
                        {{ t('wallet.bonusWarning.text') }}
                      </p>
                    </div>
                  </div>

                  <ul v-if="withdrawBonusWarning.bonuses.length > 0" class="space-y-1 text-xs text-gray-300">
                    <li
                      v-for="bonus in withdrawBonusWarning.bonuses"
                      :key="bonus.id || `${bonus.type}-${bonus.status}`"
                      class="flex items-center justify-between gap-3 rounded-lg bg-black/20 px-3 py-2"
                    >
                      <span>{{ formatWarningBonus(bonus) }}</span>
                    </li>
                  </ul>

                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
                    <button
                      type="button"
                      class="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-sm text-white transition-colors"
                      @click="cancelWithdrawWithBonusWarning"
                    >
                      {{ t('wallet.bonusWarning.cancel') }}
                    </button>
                    <button
                      type="button"
                      class="px-3 py-2 rounded-lg bg-casino-loss hover:bg-casino-loss/90 text-sm font-semibold text-white transition-colors disabled:opacity-60"
                      :disabled="loading"
                      @click="confirmWithdrawWithBonusCancellation"
                    >
                      {{ t('wallet.bonusWarning.confirm') }}
                    </button>
                  </div>
                </div>

                <div v-if="externalResult?.url && !isCryptoCheckoutVisible" class="text-sm text-center">
                  <a
                    :href="externalResult.url"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-casino-primary-light underline"
                  >
                    {{ t('wallet.openPaymentLink') }}
                  </a>
                </div>

                <div v-if="externalResult?.payAddress && !isCryptoCheckoutVisible" class="rounded-xl border border-white/10 bg-black/20 p-4">
                  <p class="text-xs text-gray-400 mb-1">{{ t('wallet.paymentAddress') }}</p>
                  <p class="text-sm text-white break-all">{{ externalResult.payAddress }}</p>
                  <div v-if="cryptoQrCodeUrl" class="mt-3 flex justify-center">
                    <img
                      :src="cryptoQrCodeUrl"
                      :alt="t('wallet.crypto.qrPaymentAlt')"
                      class="w-40 h-40 rounded-lg border border-white/10 bg-white p-2"
                    />
                  </div>
                  <p class="text-xs text-gray-400 mt-2" v-if="externalResult.payAmount || externalResult.payCurrency">
                    {{ t('wallet.amountLine', { amount: `${externalResult.payAmount || '--'} ${externalResult.payCurrency || ''}`.trim() }) }}
                  </p>
                </div>

                <div v-if="externalResult?.bankAccount" class="rounded-xl border border-white/10 bg-black/20 p-4 space-y-2">
                  <p class="text-xs text-gray-400">{{ t('wallet.bank.title') }}</p>
                  <p class="text-sm text-white break-all">
                    {{ t('wallet.bank.account') }}: {{ externalResult.bankAccount.accountNumber || t('common.notAvailable') }}
                  </p>
                  <p class="text-sm text-white break-all" v-if="externalResult.bankAccount.alias">
                    {{ t('wallet.bank.alias') }}: {{ externalResult.bankAccount.alias }}
                  </p>
                  <p class="text-sm text-white break-all" v-if="externalResult.bankAccount.accountHolder">
                    {{ t('wallet.bank.holder') }}: {{ externalResult.bankAccount.accountHolder }}
                  </p>
                  <p class="text-sm text-white break-all" v-if="externalResult.bankAccount.cuit">
                    {{ t('wallet.bank.cuit') }}: {{ externalResult.bankAccount.cuit }}
                  </p>
                </div>

                <button
                  @click="handleAction"
                  :disabled="!canSubmit"
                  class="w-full py-4 rounded-xl font-bold text-lg transition-all duration-200 flex items-center justify-center gap-3"
                  :class="actionButtonClass"
                >
                  <Loader2 v-if="loading" class="w-5 h-5 animate-spin" />
                  <template v-else>
                    <ArrowDownLeft v-if="activeTab === 'deposit'" class="w-5 h-5" />
                    <ArrowUpRight v-else class="w-5 h-5" />
                    <span>{{ activeTab === 'deposit' ? t('wallet.confirmDeposit') : t('wallet.confirmWithdraw') }}</span>
                  </template>
                </button>

                <div class="flex items-center gap-2 text-xs text-gray-500 justify-center pt-1">
                  <ShieldCheck class="w-4 h-4 text-casino-win" />
                  <span>{{ t('wallet.secure') }}</span>
                </div>
                </template>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { tEnum } from '@/i18n'
import { useGamesStore } from '@/stores/games'
import { useAuthStore } from '@/stores/auth'
import { useNotificationStore } from '@/stores/notifications'
import { walletAPI } from '@/services/api'
import {
  Wallet,
  X,
  ArrowDownLeft,
  ArrowUpRight,
  ArrowLeft,
  CheckCircle2,
  ShieldCheck,
  Loader2,
  Coins,
  Receipt,
  AlertCircle,
  ChevronDown,
} from 'lucide-vue-next'
import QRCode from 'qrcode'
import SkeletonBlock from '@/components/SkeletonBlock.vue'
import {
  getDynamicFields,
  normalizeCurrencyCode,
  requiresDestinationTag,
  resolveProviderFlow,
  validateDynamicFieldValue,
} from '@/config/wallet-provider-rules'

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
  initialTab: {
    type: String,
    default: 'deposit',
  },
})

const emit = defineEmits(['close', 'success'])

const { t } = useI18n()
const gamesStore = useGamesStore()
const authStore = useAuthStore()
const notificationStore = useNotificationStore()

const activeTab = ref(props.initialTab)
const selectedMethod = ref('')
const amount = ref('')
const loading = ref(false)
const methodsLoading = ref(false)
const methodsError = ref('')
const formError = ref('')
const formSuccess = ref('')
const withdrawBonusWarning = ref(null)
const walletMethods = ref([])
const fieldValues = ref({})
const externalResult = ref(null)
const cryptoQrCodeUrl = ref('')
const cryptoCheckoutActive = ref(false)

const QUICK_AMOUNT_BASE = [20, 50, 100, 200, 500, 1000, 2000, 3000, 5000, 10000]

const cryptoLoading = ref(false)
const cryptoCurrencies = ref([])
const selectedCryptoTicker = ref('')
const showCryptoOptions = ref(false)
const imageLoadErrors = ref({})
const manualProofDocument = ref('')
const manualProofFileName = ref('')
const manualProofLoading = ref(false)
const manualProofError = ref('')
const manualCopiedField = ref('')
const showManualTransferDetails = ref(false)
const selectedManualConfig = ref(null)
const isMobileLayout = ref(false)
const mobilePanel = ref('methods')

const parseActions = (value) => {
  if (Array.isArray(value)) {
    return [...new Set(
      value
        .map((entry) => String(entry || '').trim().toLowerCase())
        .filter((entry) => entry === 'deposit' || entry === 'withdraw'),
    )]
  }

  const normalized = String(value || '').trim().toUpperCase()
  if (normalized === 'DEPOSIT') return ['deposit']
  if (normalized === 'WITHDRAW') return ['withdraw']
  if (normalized === 'DEPOSIT_AND_WITHDRAW') return ['deposit', 'withdraw']
  return []
}

const inferActionsFromType = (providerType) => {
  const actions = []
  const depositType = typeof providerType === 'string' ? providerType : providerType?.deposit
  const withdrawType = typeof providerType === 'string' ? providerType : providerType?.withdraw

  if (String(depositType || '').trim()) actions.push('deposit')
  if (String(withdrawType || '').trim()) actions.push('withdraw')

  return [...new Set(actions)]
}

const normalizeLimit = (limit = {}) => ({
  c: normalizeCurrencyCode(limit?.c),
  deposit: {
    min: Number(limit?.deposit?.min ?? 0),
    max: Number(limit?.deposit?.max ?? 0),
  },
  withdraw: {
    min: Number(limit?.withdraw?.min ?? 0),
    max: Number(limit?.withdraw?.max ?? 0),
  },
  config: {
    manual: Array.isArray(limit?.config?.manual)
      ? limit.config.manual
        .filter((entry) => entry && typeof entry === 'object')
        .map((entry) => ({
          cbu: String(entry.cbu || '').trim(),
          cuitl: String(entry.cuitl || '').trim(),
          name: String(entry.name || '').trim(),
        }))
      : [],
  },
})

const normalizeLimitCollection = (input) => {
  if (Array.isArray(input)) {
    return input
      .filter((entry) => entry && typeof entry === 'object')
      .map(normalizeLimit)
      .filter((entry) => Boolean(entry.c))
  }

  if (input && typeof input === 'object') {
    const normalized = normalizeLimit(input)
    return normalized.c ? [normalized] : []
  }

  return []
}

const toCardSubtitle = ({ providerName, channelCode, description }) => {
  if (channelCode) {
    const cleanDescription = String(description || '').trim()
    return cleanDescription || providerName
  }
  return providerName
}

const buildMethods = (providers = []) => {
  const methods = []

  for (const provider of providers) {
    const providerCode = String(provider?.pp?.c || '').trim().toUpperCase()
    const providerName = String(provider?.pp?.n || providerCode || t('wallet.methodFallback'))
    const providerId = String(provider?.p || '')
    const providerType = provider?.pp?.t || {}
    const providerActions = parseActions(provider?.pp?.at)
    const fallbackActions = inferActionsFromType(providerType)
    const providerLimits = normalizeLimitCollection(provider?.l)
    const rawChannels = Array.isArray(provider?.pp?.ch) ? provider.pp.ch : []
    const channels = rawChannels.filter((channel) => channel?.e !== false)

    if (channels.length > 0) {
      for (const channel of channels) {
        const channelCode = String(channel?.v || '').trim().toUpperCase()
        const channelName = String(channel?.n || providerName)
        const channelActions = parseActions(channel?.at)
        const actionsSource = channelActions.length > 0
          ? channelActions
          : (providerActions.length > 0 ? providerActions : fallbackActions)
        const actions = [...new Set(actionsSource)]
        const channelLimits = normalizeLimitCollection(channel?.l)
        const limits = channelLimits.length > 0 ? channelLimits : providerLimits
        const availableCurrencies = [...new Set(limits.map((limit) => normalizeCurrencyCode(limit.c)).filter(Boolean))]
        const description = String(channel?.ds || provider?.pp?.ds || t('wallet.paymentMethodFallback'))
        const depositFlow = resolveProviderFlow({ providerCode, operation: 'deposit', providerType })
        const withdrawFlow = resolveProviderFlow({ providerCode, operation: 'withdraw', providerType })

        methods.push({
          id: `${providerId}:${channelCode || 'DEFAULT'}`,
          paymentId: providerId,
          providerCode,
          providerName,
          channelCode: channelCode || null,
          channelName,
          title: channelName,
          cardSubtitle: toCardSubtitle({ providerName, channelCode, description }),
          description,
          image: String(provider?.pp?.img || '').trim() || null,
          actions,
          limits,
          availableCurrencies,
          flow: {
            deposit: depositFlow,
            withdraw: withdrawFlow,
          },
          fields: {
            deposit: getDynamicFields({ providerCode, operation: 'deposit', channelCode, flow: depositFlow }),
            withdraw: getDynamicFields({ providerCode, operation: 'withdraw', channelCode, flow: withdrawFlow }),
          },
        })
      }
      continue
    }

    const actions = providerActions.length > 0 ? providerActions : fallbackActions
    const availableCurrencies = [...new Set(providerLimits.map((limit) => normalizeCurrencyCode(limit.c)).filter(Boolean))]
    const description = String(provider?.pp?.ds || t('wallet.paymentMethodFallback'))
    const depositFlow = resolveProviderFlow({ providerCode, operation: 'deposit', providerType })
    const withdrawFlow = resolveProviderFlow({ providerCode, operation: 'withdraw', providerType })

    methods.push({
      id: `${providerId}:DEFAULT`,
      paymentId: providerId,
      providerCode,
      providerName,
      channelCode: null,
      channelName: providerName,
      title: providerName,
      cardSubtitle: toCardSubtitle({ providerName, channelCode: null, description }),
      description,
      image: String(provider?.pp?.img || '').trim() || null,
      actions,
      limits: providerLimits,
      availableCurrencies,
      flow: {
        deposit: depositFlow,
        withdraw: withdrawFlow,
      },
      fields: {
        deposit: getDynamicFields({ providerCode, operation: 'deposit', channelCode: null, flow: depositFlow }),
        withdraw: getDynamicFields({ providerCode, operation: 'withdraw', channelCode: null, flow: withdrawFlow }),
      },
    })
  }

  return methods
}

const userCurrency = computed(() => normalizeCurrencyCode(gamesStore.currency || authStore.player?.currency || 'ARS'))

const displayBalance = computed(() => {
  const liveBalance = Number(gamesStore.balance)
  if (Number.isFinite(liveBalance)) {
    return liveBalance
  }
  const cachedBalance = Number(authStore.player?.balance)
  return Number.isFinite(cachedBalance) ? cachedBalance : 0
})

const currencySymbol = computed(() => {
  const symbols = { ARS: '$', USD: '$', EUR: '€', BRL: 'R$', MXN: '$', CLP: '$', COP: '$' }
  return symbols[userCurrency.value] || '$'
})

const filteredMethods = computed(() => {
  const tab = activeTab.value

  return walletMethods.value
    .filter((method) => method.actions.includes(tab))
    .map((method) => {
      const flow = method.flow?.[tab] || 'unsupported'
      let disabled = false
      let disabledReason = ''

      if (flow === 'unsupported') {
        disabled = true
        disabledReason = t('wallet.errors.operationUnavailable')
      }

      if (!disabled && method.availableCurrencies.length > 0 && !method.availableCurrencies.includes(userCurrency.value)) {
        disabled = true
        disabledReason = t('wallet.errors.currencyNotAllowed', { currency: userCurrency.value })
      }

      return {
        ...method,
        disabled,
        disabledReason,
      }
    })
})

const selectedMethodInfo = computed(() => filteredMethods.value.find((method) => method.id === selectedMethod.value) || null)
const showSidebar = computed(() => !isMobileLayout.value || mobilePanel.value === 'methods')
const showFormPanel = computed(() => !isMobileLayout.value || mobilePanel.value === 'form')

const currentLimit = computed(() => {
  if (!selectedMethodInfo.value) return null

  const exactLimit = selectedMethodInfo.value.limits.find((limit) => normalizeCurrencyCode(limit.c) === userCurrency.value)
  return exactLimit || selectedMethodInfo.value.limits[0] || null
})

const currentMin = computed(() => {
  if (!currentLimit.value) return 0

  const value = activeTab.value === 'deposit'
    ? Number(currentLimit.value.deposit?.min || 0)
    : Number(currentLimit.value.withdraw?.min || 0)

  return Number.isFinite(value) ? value : 0
})

const currentMax = computed(() => {
  if (!currentLimit.value) {
    return activeTab.value === 'withdraw' ? displayBalance.value : Number.POSITIVE_INFINITY
  }

  const value = activeTab.value === 'deposit'
    ? Number(currentLimit.value.deposit?.max || 0)
    : Number(currentLimit.value.withdraw?.max || 0)

  if (!Number.isFinite(value) || value <= 0) {
    return activeTab.value === 'withdraw' ? displayBalance.value : Number.POSITIVE_INFINITY
  }

  return value
})

const maxAmountAllowed = computed(() => {
  if (activeTab.value === 'withdraw') {
    const bounded = Number.isFinite(currentMax.value)
      ? Math.min(displayBalance.value, currentMax.value)
      : displayBalance.value
    return Math.max(0, bounded)
  }

  return currentMax.value
})

const maxAmountAttribute = computed(() => (Number.isFinite(maxAmountAllowed.value) ? maxAmountAllowed.value : undefined))

const quickAmounts = computed(() => {
  const min = Math.max(0, Number(currentMin.value || 0))
  const max = Number.isFinite(maxAmountAllowed.value) ? Number(maxAmountAllowed.value) : Number.POSITIVE_INFINITY

  if (Number.isFinite(max) && max <= 0) {
    return []
  }

  const base = QUICK_AMOUNT_BASE.filter((value) => value >= min && value <= max)
  if (base.length > 0) {
    return base.slice(0, 4)
  }

  const dynamic = [min, min * 2, min * 5, min * 10]
    .map((value) => Number(Number(value).toFixed(2)))
    .filter((value) => Number.isFinite(value) && value > 0 && value <= max)
  const uniqueDynamic = [...new Set(dynamic)]
  if (uniqueDynamic.length > 0) {
    return uniqueDynamic.slice(0, 4)
  }

  if (Number.isFinite(max) && max > 0) {
    return [Number(Number(Math.max(1, max)).toFixed(2))]
  }

  return []
})

const dynamicFields = computed(() => selectedMethodInfo.value?.fields?.[activeTab.value] || [])

const selectedBaseFlow = computed(() => selectedMethodInfo.value?.flow?.[activeTab.value] || 'unsupported')

const selectedFlow = computed(() => selectedBaseFlow.value)

const totalAmount = computed(() => {
  const baseAmount = Number(amount.value || 0)
  if (!Number.isFinite(baseAmount)) return '0.00'
  return baseAmount.toFixed(2)
})

const isCryptoFlow = computed(() => selectedFlow.value === 'crypto')
const showManualProofUploader = computed(() => activeTab.value === 'deposit' && selectedFlow.value === 'manual')
const isCryptoCheckoutVisible = computed(() => cryptoCheckoutActive.value)

const selectedCrypto = computed(() => (
  cryptoCurrencies.value.find((item) => normalizeCurrencyCode(item.ticker) === normalizeCurrencyCode(selectedCryptoTicker.value)) || null
))
const selectedCryptoLabel = computed(() => {
  if (!selectedCrypto.value) return ''
  return `${selectedCrypto.value.fullName || selectedCrypto.value.ticker} (${selectedCrypto.value.ticker})`
})

const pickRandomManualConfig = (manualList = []) => {
  if (!Array.isArray(manualList) || manualList.length === 0) {
    return null
  }

  const idx = Math.floor(Math.random() * manualList.length)
  const picked = manualList[idx] || manualList[0] || {}
  return {
    cbu: String(picked.cbu || '').trim() || null,
    cuitl: String(picked.cuitl || '').trim() || null,
    casinoHolder: String(picked.name || '').trim() || null,
  }
}

const manualDepositBankInfo = computed(() => {
  if (selectedManualConfig.value) {
    return selectedManualConfig.value
  }

  const manual = currentLimit.value?.config?.manual
  if (!Array.isArray(manual) || manual.length === 0) {
    return null
  }

  const first = manual[0] || {}
  return {
    cbu: String(first.cbu || '').trim() || null,
    cuitl: String(first.cuitl || '').trim() || null,
    casinoHolder: String(first.name || '').trim() || null,
  }
})

const manualDepositBankFields = computed(() => {
  const info = manualDepositBankInfo.value
  if (!info) {
    return []
  }

  const entries = [
    { key: 'cbu', label: t('wallet.bank.account'), value: info.cbu },
    { key: 'casinoHolder', label: t('wallet.bank.holder'), value: info.casinoHolder },
    { key: 'cuitl', label: t('wallet.bank.cuit'), value: info.cuitl },
  ]

  return entries.filter((entry) => String(entry.value || '').trim() !== '')
})

const showPayoutTag = computed(() => requiresDestinationTag(selectedCryptoTicker.value))

const validateAmountValue = () => {
  const numericAmount = Number(amount.value)
  if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
    return t('wallet.errors.invalidAmount')
  }
  if (numericAmount < currentMin.value) {
    return t('wallet.errors.minAmount', { amount: `${currencySymbol.value}${currentMin.value}` })
  }
  if (Number.isFinite(maxAmountAllowed.value) && numericAmount > maxAmountAllowed.value) {
    return t('wallet.errors.maxAmount', { amount: `${currencySymbol.value}${maxAmountAllowed.value}` })
  }
  return ''
}

const validateDynamicFields = () => {
  for (const field of dynamicFields.value) {
    const validationError = validateDynamicFieldValue({
      field,
      value: fieldValues.value[field.key],
    })
    if (validationError) {
      return validationError
    }
  }
  return ''
}

const validateForm = () => {
  if (!selectedMethodInfo.value || selectedMethodInfo.value.disabled) {
    return t('wallet.errors.methodUnavailable')
  }

  if (selectedFlow.value === 'unsupported') {
    return t('wallet.errors.methodUnavailable')
  }

  const amountError = validateAmountValue()
  if (amountError) {
    return amountError
  }

  const dynamicError = validateDynamicFields()
  if (dynamicError) {
    return dynamicError
  }

  if (isCryptoFlow.value) {
    if (!selectedCryptoTicker.value) {
      return t('wallet.errors.selectCrypto')
    }
    if (activeTab.value === 'withdraw') {
      if (!String(fieldValues.value.payoutAddress || '').trim()) {
        return t('wallet.errors.addressRequired')
      }
      if (showPayoutTag.value && !String(fieldValues.value.payoutExtraId || '').trim()) {
        return t('wallet.errors.tagRequired')
      }
    }
  }

  return ''
}

const canSubmit = computed(() => {
  if (loading.value) return false
  return !validateForm()
})

const actionButtonClass = computed(() => {
  if (loading.value) return 'bg-white/10 text-gray-400 cursor-wait'
  if (!canSubmit.value) return 'bg-white/10 text-gray-500 cursor-not-allowed'
  if (activeTab.value === 'deposit') {
    return 'bg-casino-win hover:bg-casino-win-dark text-black shadow-lg shadow-casino-win/20'
  }
  return 'bg-casino-loss hover:bg-casino-loss-dark text-white shadow-lg shadow-casino-loss/20'
})

const resolveMethodImage = (method) => {
  if (!method?.id) return null
  const image = String(method.image || '').trim()
  if (!image) return null
  return imageLoadErrors.value[method.id] ? null : image
}

const markImageError = (method) => {
  if (!method?.id) return
  imageLoadErrors.value = {
    ...imageLoadErrors.value,
    [method.id]: true,
  }
}

const isQuickAmountDisabled = (quickAmount) => {
  if (!selectedMethodInfo.value || selectedMethodInfo.value.disabled) return true
  if (activeTab.value === 'withdraw' && quickAmount > displayBalance.value) return true
  if (Number.isFinite(maxAmountAllowed.value) && quickAmount > maxAmountAllowed.value) return true
  return false
}

const clearManualProof = () => {
  manualProofDocument.value = ''
  manualProofFileName.value = ''
  manualProofLoading.value = false
  manualProofError.value = ''
  manualCopiedField.value = ''
}

const updateResponsiveMode = () => {
  isMobileLayout.value = typeof window !== 'undefined' ? window.innerWidth < 1024 : false
  if (!isMobileLayout.value) {
    mobilePanel.value = 'form'
  }
}

const startNewCryptoDeposit = () => {
  amount.value = ''
  fieldValues.value = {}
  cryptoCheckoutActive.value = false
  showCryptoOptions.value = false
  externalResult.value = null
  cryptoQrCodeUrl.value = ''
  formError.value = ''
  formSuccess.value = ''
  withdrawBonusWarning.value = null
}

const normalizeWithdrawBonusWarning = (payload) => {
  const source = payload?.data || payload || {}
  const bonuses = Array.isArray(source.bonuses) ? source.bonuses : []
  return {
    requiresConfirmation: source.requiresConfirmation === true,
    confirmationField: source.confirmationField || 'confirmCancelActiveBonuses',
    bonuses: bonuses.map((bonus) => ({
      id: String(bonus.id || bonus._id || ''),
      type: String(bonus.type || 'BONUS').toUpperCase(),
      status: String(bonus.status || '').toUpperCase(),
      amount: Number(bonus.amount ?? bonus.bonusAmount ?? 0),
      bonusBalance: Number(bonus.bonusBalance ?? 0),
      lockedBonusBalance: Number(bonus.lockedBonusBalance ?? 0),
    })),
  }
}

const formatWarningBonus = (bonus) => {
  const pieces = [tEnum('bonuses.type', bonus.type)]
  if (bonus.status) pieces.push(tEnum('bonuses.status', bonus.status))
  const totalBonusBalance = Number(bonus.bonusBalance || 0) + Number(bonus.lockedBonusBalance || 0)
  if (totalBonusBalance > 0) {
    pieces.push(t('wallet.bonusWarning.balance', { amount: `${currencySymbol.value}${totalBonusBalance.toFixed(2)}` }))
  }
  return pieces.join(' · ')
}

const clearWithdrawBonusWarning = () => {
  withdrawBonusWarning.value = null
}

const cancelWithdrawWithBonusWarning = () => {
  withdrawBonusWarning.value = null
  formError.value = ''
  formSuccess.value = ''
  loading.value = false
}

const confirmWithdrawWithBonusCancellation = () => {
  handleAction({ confirmCancelActiveBonuses: true })
}

const selectCryptoTicker = (ticker) => {
  selectedCryptoTicker.value = normalizeCurrencyCode(ticker)
  showCryptoOptions.value = false
}

const copyManualField = async ({ key, value }) => {
  const text = String(value || '').trim()
  if (!text) {
    return
  }

  try {
    await navigator.clipboard.writeText(text)
    manualCopiedField.value = key
    setTimeout(() => {
      if (manualCopiedField.value === key) {
        manualCopiedField.value = ''
      }
    }, 1400)
  } catch (_err) {
    notificationStore.error(t('wallet.errors.copyFailed'))
  }
}

const handleManualProofUpload = async (event) => {
  const file = event?.target?.files?.[0]
  if (event?.target) {
    event.target.value = ''
  }

  if (!file) return

  const maxBytes = 10 * 1024 * 1024
  if (Number(file.size || 0) > maxBytes) {
    manualProofError.value = t('wallet.errors.fileTooLarge')
    return
  }

  manualProofLoading.value = true
  manualProofError.value = ''

  try {
    const dataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(String(reader.result || ''))
      reader.onerror = () => reject(new Error(t('wallet.errors.fileRead')))
      reader.readAsDataURL(file)
    })

    manualProofDocument.value = String(dataUrl || '')
    manualProofFileName.value = String(file.name || t('wallet.manual.fileFallback'))
  } catch (_err) {
    manualProofDocument.value = ''
    manualProofFileName.value = ''
    manualProofError.value = t('wallet.errors.fileProcess')
  } finally {
    manualProofLoading.value = false
  }
}

const resetFormState = () => {
  selectedMethod.value = ''
  amount.value = ''
  loading.value = false
  formError.value = ''
  formSuccess.value = ''
  withdrawBonusWarning.value = null
  fieldValues.value = {}
  cryptoCheckoutActive.value = false
  externalResult.value = null
  cryptoQrCodeUrl.value = ''
  cryptoLoading.value = false
  cryptoCurrencies.value = []
  selectedCryptoTicker.value = ''
  showCryptoOptions.value = false
  selectedManualConfig.value = null
  showManualTransferDetails.value = false
  mobilePanel.value = 'methods'
  clearManualProof()
}

const resetLocalCaches = () => {
  imageLoadErrors.value = {}
}

const ensureSelectedMethod = () => {
  if (filteredMethods.value.length === 0) {
    selectedMethod.value = ''
    return
  }

  const stillExists = filteredMethods.value.some((method) => method.id === selectedMethod.value)
  if (stillExists) return

  if (isMobileLayout.value) {
    selectedMethod.value = ''
    return
  }

  const firstEnabled = filteredMethods.value.find((method) => !method.disabled)
  selectedMethod.value = firstEnabled?.id || filteredMethods.value[0]?.id || ''
}

const selectMethod = (method) => {
  if (method.disabled) return
  selectedMethod.value = method.id
  if (isMobileLayout.value) {
    mobilePanel.value = 'form'
  }
}

const sanitizePayload = (payload) => {
  const out = {}
  for (const [key, value] of Object.entries(payload || {})) {
    if (value === undefined || value === null) continue
    if (typeof value === 'string' && value.trim() === '') continue
    out[key] = typeof value === 'string' ? value.trim() : value
  }
  return out
}

const hydrateCryptoCurrencies = (list = []) => {
  cryptoCurrencies.value = list.map((item) => ({
    ...item,
    ticker: normalizeCurrencyCode(item?.ticker),
    symbol: normalizeCurrencyCode(item?.symbol || item?.ticker),
  }))

  if (cryptoCurrencies.value.length === 0) {
    selectedCryptoTicker.value = ''
    return
  }

  const stillValid = cryptoCurrencies.value.some((item) => item.ticker === selectedCryptoTicker.value)
  if (!stillValid) {
    selectedCryptoTicker.value = cryptoCurrencies.value[0].ticker
  }
}

const refreshCryptoContext = async () => {
  cryptoLoading.value = false
  cryptoCurrencies.value = []
  selectedCryptoTicker.value = ''
  showCryptoOptions.value = false

  const method = selectedMethodInfo.value
  if (!method || method.disabled) {
    return
  }

  if (selectedFlow.value !== 'crypto') {
    return
  }

  cryptoLoading.value = true
  formError.value = ''
  const currenciesResponse = await walletAPI.cryptoCurrencies({
    paymentId: method.paymentId,
  })

  if (!currenciesResponse.success) {
    cryptoLoading.value = false
    formError.value = currenciesResponse?.error?.message || t('wallet.errors.cryptoLoad')
    return
  }

  const payload = currenciesResponse.data
  const list = Array.isArray(payload)
    ? payload
    : (Array.isArray(payload?.data) ? payload.data : [])

  if (list.length === 0) {
    cryptoLoading.value = false
    formError.value = t('wallet.errors.cryptoNone')
    return
  }

  hydrateCryptoCurrencies(list)
  cryptoLoading.value = false
}

const runCryptoLimitCheck = async ({ amountValue, transactionType }) => {
  if (!selectedMethodInfo.value || !selectedCryptoTicker.value) {
    return { success: false, error: { message: t('wallet.errors.selectCrypto') } }
  }

  const response = await walletAPI.cryptoCheckLimit({
    paymentId: selectedMethodInfo.value.paymentId,
    currencyFrom: userCurrency.value,
    currencyTo: selectedCryptoTicker.value,
    transactionType,
    amount: amountValue,
  })

  if (!response.success) {
    return response
  }

  const payload = response.data
  const details = payload?.data && typeof payload.data === 'object' ? payload.data : payload

  if (details && details.isValid === false) {
    return {
      success: false,
      error: {
        message: t('wallet.errors.cryptoBelowMin', { amount: `${details.minLimitFiat || '--'} ${userCurrency.value}` }),
      },
    }
  }

  return { success: true, data: details }
}

const refreshWalletData = async () => {
  methodsLoading.value = true
  methodsError.value = ''

  const [providersResult, balanceResult] = await Promise.all([
    walletAPI.getProviders(),
    walletAPI.getBalance(),
  ])

  if (!providersResult.success) {
    methodsLoading.value = false
    methodsError.value = providersResult.error.message
    walletMethods.value = []
    return
  }

  walletMethods.value = buildMethods(providersResult.data)

  if (balanceResult.success) {
    const nextBalance = Number(balanceResult.data.balance)
    if (Number.isFinite(nextBalance)) {
      gamesStore.balance = nextBalance
      gamesStore.balanceLoaded = true
    }

    const nextCurrency = normalizeCurrencyCode(balanceResult.data.currency || userCurrency.value)
    if (nextCurrency) {
      gamesStore.currency = nextCurrency
    }
  }

  methodsLoading.value = false
  ensureSelectedMethod()
}

const isActiveBonusConfirmationError = (response) => {
  const codeString = String(response?.error?.codeString || response?.error?.payload?.code_string || '').toUpperCase()
  return codeString === 'ACTIVE_BONUSES_CONFIRMATION_REQUIRED'
}

const handleAction = async (options = {}) => {
  if (loading.value || !selectedMethodInfo.value) return

  const confirmCancelActiveBonuses = options?.confirmCancelActiveBonuses === true
  formError.value = ''
  formSuccess.value = ''
  externalResult.value = null
  if (!confirmCancelActiveBonuses) {
    clearWithdrawBonusWarning()
  }

  const validationError = validateForm()
  if (validationError) {
    formError.value = validationError
    return
  }

  loading.value = true

  const amountValue = Number(amount.value)
  const method = selectedMethodInfo.value
  const operation = activeTab.value
  const flow = selectedFlow.value

  const basePayload = {
    paymentId: method.paymentId,
    amount: amountValue,
    currency: userCurrency.value,
    channel: method.channelCode || undefined,
  }

  const dynamicPayload = sanitizePayload(fieldValues.value)
  const withdrawConfirmationPayload = operation === 'withdraw' && confirmCancelActiveBonuses
    ? { confirmCancelActiveBonuses: true }
    : {}

  let response

  if (operation === 'deposit' && flow === 'manual') {
    const manualInfo = manualDepositBankInfo.value
    response = await walletAPI.depositCreate({
      ...basePayload,
      ...dynamicPayload,
      proofDocument: manualProofDocument.value || undefined,
      reference: '0000',
      info: manualInfo
        ? {
          cbu: manualInfo.cbu,
          cuitl: manualInfo.cuitl,
          casinoHolder: manualInfo.casinoHolder,
        }
        : undefined,
    })
  } else if (operation === 'deposit' && flow === 'external') {
    response = await walletAPI.depositExternalCreate({
      ...basePayload,
      ...dynamicPayload,
    })
  } else if (operation === 'deposit' && flow === 'crypto') {
    const limitResult = await runCryptoLimitCheck({
      amountValue,
      transactionType: 'deposit',
    })

    if (!limitResult.success) {
      formError.value = limitResult.error.message
      loading.value = false
      return
    }

    response = await walletAPI.depositExternalCreate({
      ...basePayload,
      ...dynamicPayload,
      payCurrency: selectedCryptoTicker.value,
      cryptoCurrency: selectedCrypto.value?.symbol || selectedCryptoTicker.value,
    })
  } else if (operation === 'withdraw' && flow === 'manual') {
    response = await walletAPI.withdrawCreate({
      ...basePayload,
      ...dynamicPayload,
      ...withdrawConfirmationPayload,
    })
  } else if (operation === 'withdraw' && flow === 'external') {
    response = await walletAPI.withdrawExternalCreate({
      ...basePayload,
      ...dynamicPayload,
      ...withdrawConfirmationPayload,
    })
  } else if (operation === 'withdraw' && flow === 'crypto') {
    const limitResult = await runCryptoLimitCheck({
      amountValue,
      transactionType: 'withdrawal',
    })

    if (!limitResult.success) {
      formError.value = limitResult.error.message
      loading.value = false
      return
    }

    response = await walletAPI.withdrawExternalCreate({
      ...basePayload,
      ...withdrawConfirmationPayload,
      payoutCurrency: selectedCryptoTicker.value,
      payoutAddress: String(fieldValues.value.payoutAddress || '').trim(),
      payoutExtraId: showPayoutTag.value ? String(fieldValues.value.payoutExtraId || '').trim() : undefined,
    })
  } else {
    formError.value = t('wallet.errors.methodUnavailable')
    loading.value = false
    return
  }

  loading.value = false

  if (!response?.success) {
    if (operation === 'withdraw' && isActiveBonusConfirmationError(response)) {
      withdrawBonusWarning.value = normalizeWithdrawBonusWarning(response.error?.payload)
      return
    }

    formError.value = response?.error?.message || t('wallet.errors.operationFailed')
    return
  }

  clearWithdrawBonusWarning()

  let responseData = response.data || null

  if (operation === 'deposit' && flow === 'external' && !isCryptoFlow.value) {
    let checkoutUrl = responseData?.url || responseData?.payUrl || responseData?.redirect_url

    // AlphaGateway can persist its checkout URL on the payment while the create
    // response only returns txid. Recover it from the existing wallet payments API.
    if (!checkoutUrl && responseData?.txid) {
      const paymentsResponse = await walletAPI.getPayments({ page: 1, limit: 20, type: 'deposit', status: 'PENDING' })
      const payment = paymentsResponse.success
        ? paymentsResponse.data?.items?.find((item) => String(item?._id || item?.txid) === String(responseData.txid))
        : null
      checkoutUrl = payment?.i?.info?.payUrl || payment?.i?.payUrl || null
    }

    if (checkoutUrl) {
      responseData = { ...responseData, url: checkoutUrl }
    }

    const hasExpectedPayload = Boolean(responseData?.url || responseData?.bankAccount)
    if (!hasExpectedPayload) {
      formError.value = t('wallet.errors.providerNoData')
      return
    }
  }

  externalResult.value = responseData
  cryptoQrCodeUrl.value = ''
  cryptoCheckoutActive.value = false

  if (externalResult.value?.payAddress) {
    try {
      cryptoQrCodeUrl.value = await QRCode.toDataURL(String(externalResult.value.payAddress))
    } catch (_err) {
      cryptoQrCodeUrl.value = ''
    }
  }

  if (operation === 'deposit' && flow === 'crypto' && !externalResult.value?.payAddress && !externalResult.value?.url) {
    formError.value = t('wallet.errors.cryptoInvalidData')
    externalResult.value = null
    cryptoQrCodeUrl.value = ''
    cryptoCheckoutActive.value = false
    return
  }

  if (operation === 'deposit' && flow === 'crypto') {
    cryptoCheckoutActive.value = true
  }

  if (responseData?.url) {
    window.open(responseData.url, '_blank', 'noopener,noreferrer')
  }

  await gamesStore.fetchBalance()

  if (activeTab.value === 'deposit') {
    if (flow === 'crypto') {
      formSuccess.value = t('wallet.success.crypto')
    } else if (flow === 'external') {
      amount.value = ''
      fieldValues.value = {}
      clearManualProof()
      notificationStore.depositSuccess(amountValue)
      formSuccess.value = t('wallet.success.external')
    } else {
      amount.value = ''
      fieldValues.value = {}
      externalResult.value = null
      cryptoQrCodeUrl.value = ''
      selectedCryptoTicker.value = ''
      clearManualProof()
      notificationStore.depositSuccess(amountValue)
      formSuccess.value = t('wallet.success.deposit')
    }
  } else {
    notificationStore.withdrawalSuccess(amountValue)
    formSuccess.value = t('wallet.success.withdraw')
  }

  emit('success', {
    type: activeTab.value,
    amount: amountValue,
    method: method.providerCode,
    tx: response.data?.txid || null,
  })

  const shouldAutoClose = flow === 'manual' || operation === 'withdraw'

  if (shouldAutoClose) {
    setTimeout(() => {
      closeModal()
    }, 900)
  }
}

const closeModal = () => {
  emit('close')
}

watch(activeTab, () => {
  amount.value = ''
  formError.value = ''
  formSuccess.value = ''
  clearWithdrawBonusWarning()
  showCryptoOptions.value = false
  cryptoCheckoutActive.value = false
  externalResult.value = null
  cryptoQrCodeUrl.value = ''
  clearManualProof()
  if (isMobileLayout.value) {
    mobilePanel.value = 'methods'
  }
  ensureSelectedMethod()
})

watch(() => props.initialTab, (newTab) => {
  activeTab.value = newTab
})

watch(filteredMethods, () => {
  ensureSelectedMethod()
})

watch([selectedMethodInfo, activeTab], async () => {
  selectedManualConfig.value = pickRandomManualConfig(currentLimit.value?.config?.manual)
  showManualTransferDetails.value = false
  fieldValues.value = {}
  formError.value = ''
  formSuccess.value = ''
  clearWithdrawBonusWarning()
  showCryptoOptions.value = false
  cryptoCheckoutActive.value = false
  externalResult.value = null
  cryptoQrCodeUrl.value = ''
  clearManualProof()
  await refreshCryptoContext()
})

watch(() => props.isOpen, async (isOpen) => {
  if (!isOpen) {
    resetFormState()
    return
  }

  resetFormState()
  resetLocalCaches()
  updateResponsiveMode()
  activeTab.value = props.initialTab
  await refreshWalletData()
  await refreshCryptoContext()
})

onMounted(() => {
  updateResponsiveMode()
  if (typeof window !== 'undefined') {
    window.addEventListener('resize', updateResponsiveMode)
  }
})

onBeforeUnmount(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('resize', updateResponsiveMode)
  }
})
</script>

<style scoped>
.wallet-panels-grid {
  height: 100%;
  min-height: 0;
}

.wallet-sidebar {
  min-height: 0;
  max-height: 100%;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
}

.wallet-methods-scroll {
  min-height: 0;
  max-height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-gutter: stable;
  scrollbar-width: thin;
  scrollbar-color: rgba(148, 163, 184, 0.45) transparent;
}

.wallet-form-panel {
  min-height: 0;
  max-height: 100%;
}

.wallet-methods-scroll::-webkit-scrollbar {
  width: 10px;
}

.wallet-methods-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.wallet-methods-scroll::-webkit-scrollbar-thumb {
  background: rgba(148, 163, 184, 0.38);
  border-radius: 999px;
  border: 2px solid transparent;
  background-clip: content-box;
}

.wallet-methods-scroll::-webkit-scrollbar-thumb:hover {
  background: rgba(148, 163, 184, 0.62);
  background-clip: content-box;
}

.method-hero {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.method-chip {
  padding: 3px 9px;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.3);
  background: rgba(148, 163, 184, 0.1);
  color: rgba(226, 232, 240, 0.98);
  font-size: 11px;
  font-weight: 600;
  line-height: 1.2;
}

.method-chip-muted {
  border-color: rgba(148, 163, 184, 0.24);
  background: rgba(148, 163, 184, 0.05);
  color: rgba(203, 213, 225, 0.92);
}

.collapse-fade-enter-active,
.collapse-fade-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.collapse-fade-enter-from,
.collapse-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
